import React from 'react';
import groupBy from 'lodash.groupby';

import { Solicitacao } from '@/models/Solicitacao';

import { ItemComplementarSearchResult } from '@/features/prescricao-livre/services/PrescricaoLivreService';

import CardGroup, { CardGroupProps, SubGroupConfig } from '../CardGroup';
import { CardSolicitacaoProxyProps } from '../CardSolicitacao';
import AddItemComplementar, {
  SearchItemComplementarCallback,
} from '../AddItemComplementar';
import RatioBadge from '../RatioBadge';

const ITENS_COMPLEMENTARES_CATEGORIA = 'Itens complementares';
const OUTROS_CATEGORIA = 'Outros';

type GroupProps = Omit<
  CardGroupProps<Solicitacao>,
  | 'data'
  | 'keyExtractor'
  | 'groupState'
  | 'onGroupClick'
  | 'renderItem'
  | 'renderFooter'
  | 'renderSubgroupActions'
>;

interface ItensComplementaresState {
  title: string;
  enabled: boolean;
  onSearch: SearchItemComplementarCallback;
  onAdd: (item: ItemComplementarSearchResult) => void;
}

export interface CardSolicitacaoGroupProps extends GroupProps {
  items: Solicitacao[];
  selected?: Solicitacao['id'][];
  addConfig?: Partial<ItensComplementaresState>;
  readOnly?: boolean;
  // Item type
  CardSolicitacaoComponent?: React.ComponentType<CardSolicitacaoProxyProps>;
  renderGroupItem?: (
    renderProps: CardSolicitacaoProxyProps,
  ) => JSX.Element | undefined;
  // Permission flags
  hasSelectPermission: boolean;
  hasExpandPermission: boolean;
  hasAddPermission: boolean;
  hasEditPermission: boolean;
  // Events
  onItemsSelect?: (items: Solicitacao[]) => void;
  onItemSelect?: (item: Solicitacao) => void;
  onItemResourceEditStart?: (item: Solicitacao) => void;
  onItemResourceEditCancel?: (item: Solicitacao) => void;
  onItemEdit?: (item: Solicitacao, resource: fhir4.FhirResource) => void;
  onItemAdd?: (resource: fhir4.FhirResource) => void;
}

const categoria = (item: Solicitacao, defaultValue?: string | null) => {
  if (item.solicitacao_incluida) {
    return ITENS_COMPLEMENTARES_CATEGORIA;
  }

  const defaultCategoria =
    defaultValue === undefined ? OUTROS_CATEGORIA : defaultValue;

  return item.categoria ? item.categoria.trim() : defaultCategoria;
};

const groupByCategoria = (items: Solicitacao[]) => {
  if (items.some(item => !!categoria(item, null))) {
    const grouped = groupBy(items, item => categoria(item));

    const orderedEntries = Object.entries(grouped).sort(([a], [b]) => {
      const rank = (category: string) => {
        if (category === ITENS_COMPLEMENTARES_CATEGORIA) {
          return 2;
        }

        if (category === OUTROS_CATEGORIA) {
          return 1;
        }

        return 0;
      };

      return rank(a) - rank(b);
    });

    return Object.fromEntries(orderedEntries);
  }

  return items;
};

const CardSolicitacaoGroup: React.FC<CardSolicitacaoGroupProps> = ({
  groupTitle,
  groupFeedback,
  items,
  selected = [],
  addConfig: {
    title: addTitle = 'Adicionar item complementar',
    onSearch: addSearch,
  } = {},
  CardSolicitacaoComponent,
  renderGroupItem,
  hasSelectPermission = true,
  hasExpandPermission = true,
  hasAddPermission = true,
  hasEditPermission = true,
  onItemAdd,
  onItemsSelect,
  onItemSelect,
  onItemResourceEditStart,
  onItemResourceEditCancel,
  onItemEdit,
  readOnly = false,
}) => {
  const groupedItems = React.useMemo(() => groupByCategoria(items), [items]);

  const groupState = React.useMemo(() => {
    if (items.every(item => selected.includes(item.id))) {
      return 'marked';
    }

    if (items.some(item => selected.includes(item.id))) {
      return 'half-marked';
    }

    return 'unmarked';
  }, [items, selected]);

  const subgroupConfig = React.useMemo(() => {
    // Check if grouped items is dictionary
    if (typeof groupedItems === 'object' && !Array.isArray(groupedItems)) {
      return Object.entries(groupedItems).reduce(
        (acc, [subgroup, groupsItems]) => {
          if (groupsItems.every(item => selected.includes(item.id))) {
            acc[subgroup] = { state: 'marked' };
          } else if (groupsItems.some(item => selected.includes(item.id))) {
            acc[subgroup] = { state: 'half-marked' };
          } else {
            acc[subgroup] = { state: 'unmarked' };
          }

          return acc;
        },
        {} as Record<string, SubGroupConfig>,
      );
    }
    return {};
  }, [groupedItems, selected]);

  const itemState = React.useCallback(
    (item: Solicitacao) => {
      return selected.includes(item.id) ? 'marked' : 'unmarked';
    },
    [selected],
  );

  const subgroupBadge = React.useCallback(
    (subgroup: string) => {
      const group = items.filter(item => categoria(item) === subgroup);
      const groupSelected = group.filter(item => selected.includes(item.id));

      return (
        <RatioBadge
          numerator={
            groupSelected.length === 0 ? undefined : groupSelected.length
          }
          denominator={group.length}
        />
      );
    },
    [items, selected],
  );

  const handleGroupClick = React.useCallback(
    (subgroup?: string) => {
      let itemsToSelect: Solicitacao[] = [];
      if (subgroup) {
        const groupItems = items.filter(item => categoria(item) === subgroup);

        itemsToSelect = groupItems;
      } else {
        itemsToSelect = items;
      }

      if (onItemsSelect) {
        onItemsSelect(itemsToSelect);
      }
    },
    [items, onItemsSelect],
  );

  const renderAddSection = React.useCallback(() => {
    if (readOnly || !hasAddPermission) return null;

    return (
      <AddItemComplementar
        onAdd={({ data }) => onItemAdd && onItemAdd(data)}
        onSearch={addSearch}
        title={addTitle}
      />
    );
  }, [readOnly, hasAddPermission, addSearch, addTitle, onItemAdd]);

  return (
    <CardGroup
      readOnly={readOnly}
      groupTitle={groupTitle}
      groupFeedback={groupFeedback}
      groupState={groupState}
      subgroupConfig={subgroupConfig}
      onGroupClick={handleGroupClick}
      data={groupedItems}
      keyExtractor={item => item.id.toString()}
      renderSubgroupActions={subgroupBadge}
      renderItem={solicitacao => {
        const renderItemProps: CardSolicitacaoProxyProps = {
          solicitacao,
          state: itemState(solicitacao),
          disabledMode: readOnly ? 'selection' : 'none',
          canExpand: hasExpandPermission,
          canSelect: hasSelectPermission,
          canEdit: !readOnly && hasEditPermission,
          onSelect: () => onItemSelect && onItemSelect(solicitacao),
          onResourceEditStart: () =>
            onItemResourceEditStart && onItemResourceEditStart(solicitacao),
          onResourceEditCancel: () =>
            onItemResourceEditCancel && onItemResourceEditCancel(solicitacao),
          onResourceEdit: edited =>
            onItemEdit && onItemEdit(solicitacao, edited),
        };

        if (renderGroupItem) {
          return renderGroupItem(renderItemProps);
        }

        if (CardSolicitacaoComponent) {
          // eslint-disable-next-line react/jsx-props-no-spreading
          return <CardSolicitacaoComponent {...renderItemProps} />;
        }

        return undefined;
      }}
      renderFooter={renderAddSection}
    />
  );
};

export default CardSolicitacaoGroup;
